import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, HeartHandshake, Lock, Eye, EyeOff, Users2, Building2, MapPin, ExternalLink, LogOut, Check, ShieldCheck } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/shared";
import { getCommunity, joinCommunity, updateCommunityVisibility, leaveCommunity } from "@/lib/api";
import { CommunityIcon, accentClasses } from "@/lib/communityIcons";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

export default function CommunityDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useApp();
  const [data, setData] = useState(null);
  const [joinOpen, setJoinOpen] = useState(false);

  const load = () => getCommunity(slug).then(setData).catch(() => setData("notfound"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  if (data === "notfound") return <div className="min-h-screen flex flex-col items-center justify-center"><h1 className="font-heading text-2xl font-bold">Community not found</h1><Button className="mt-4" onClick={() => navigate("/communities")}>Back</Button></div>;
  if (!data) return <div className="max-w-4xl mx-auto p-8"><Skeleton className="h-40 rounded-lg" /><Skeleton className="h-64 rounded-lg mt-6" /></div>;

  const ac = accentClasses(data.accent);
  const isEmployer = currentUser?.role === "employer";
  const isWorker = currentUser?.role === "worker";
  const membership = data.my_membership;

  const toggleVisibility = async () => {
    const val = !membership.show_on_resume;
    await updateCommunityVisibility(slug, val);
    toast.success(val ? "Now visible on your resume" : "Hidden from your resume");
    load();
  };
  const leave = async () => {
    await leaveCommunity(slug);
    toast.info("You've left the community");
    load();
  };

  return (
    <div className="min-h-screen grain">
      <Header title="Community" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <button data-testid="comm-back-btn" onClick={() => navigate("/communities")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground mb-4">
          <ArrowLeft className="w-4 h-4" /> All communities
        </button>

        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className={`rounded-xl overflow-hidden border border-border`} data-testid="community-hero">
          <div className={`bg-gradient-to-br ${ac.grad} p-8 text-white`}>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg bg-white/20 flex items-center justify-center">
                <CommunityIcon name={data.icon} className="w-8 h-8" />
              </div>
              <div>
                <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">{data.name}</h1>
                <p className="mt-1 text-sm text-white/85">{data.short_desc}</p>
              </div>
            </div>
            <p className="mt-5 text-sm text-white/85 leading-relaxed max-w-2xl">{data.long_desc}</p>
            <div className="mt-5 flex items-center gap-4 text-xs text-white/80">
              <span className="inline-flex items-center gap-1"><Users2 className="w-3.5 h-3.5" />{data.member_count} members</span>
              <span className="inline-flex items-center gap-1"><HeartHandshake className="w-3.5 h-3.5" />{data.pledged_employer_count} pledged employers</span>
            </div>
          </div>

          <div className="p-5 flex flex-wrap gap-3 items-center justify-between bg-card">
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <Lock className="w-3.5 h-3.5" /> Private by default. You choose what's visible on your resume.
            </div>
            {!currentUser ? (
              <Button onClick={() => navigate("/login")}>Sign in to join</Button>
            ) : membership ? (
              <div className="flex gap-2 flex-wrap">
                {membership.role !== "pledged_employer" && (
                  <Button data-testid="toggle-resume-visibility" variant="outline" onClick={toggleVisibility}>
                    {membership.show_on_resume ? <><EyeOff className="w-4 h-4 mr-1.5" /> Hide from resume</> : <><Eye className="w-4 h-4 mr-1.5" /> Show on resume</>}
                  </Button>
                )}
                <Button data-testid="leave-community-btn" variant="ghost" onClick={leave}>
                  <LogOut className="w-4 h-4 mr-1.5" /> Leave community
                </Button>
              </div>
            ) : (
              <Button data-testid="join-community-btn" onClick={() => setJoinOpen(true)}>
                {isEmployer ? "Take the pledge" : "Join community"}
              </Button>
            )}
          </div>
        </motion.div>

        {/* Pledged employers */}
        <section className="mt-8" data-testid="pledged-employers-section">
          <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4 flex items-center gap-1.5"><HeartHandshake className="w-3.5 h-3.5" /> Pledged employers</h2>
          {data.pledged_employers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No employers have taken the pledge yet — be the first.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {data.pledged_employers.map((e) => (
                <div key={e.id} data-testid={`pledged-employer-${e.id}`} className="rounded-lg border border-border bg-card p-4 flex items-center gap-3">
                  <Avatar className="w-12 h-12 border border-border"><AvatarImage src={e.avatar_url} className="object-cover" /><AvatarFallback>{e.name?.[0]}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-bold truncate">{e.name}</div>
                    <div className="text-xs text-muted-foreground inline-flex items-center gap-1 truncate"><Building2 className="w-3 h-3" />{e.company}</div>
                  </div>
                  <span className={`inline-flex items-center gap-1 rounded-full ${ac.bg} ${ac.text} px-2.5 py-1 text-xs font-bold shrink-0`}>
                    <ShieldCheck className="w-3.5 h-3.5" /> Pledged
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Worker showcase */}
        <section className="mt-8" data-testid="worker-showcase-section">
          <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4 flex items-center gap-1.5"><Users2 className="w-3.5 h-3.5" /> Featured members</h2>
          {data.worker_showcase.length === 0 ? (
            <p className="text-sm text-muted-foreground">Featured members will appear here as workers opt-in to make their membership visible on their public resume.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {data.worker_showcase.map((w) => (
                <Link key={w.id} to={`/resume/${w.id}`} data-testid={`showcase-worker-${w.id}`}
                      className="rounded-lg border border-border bg-card p-4 hover:border-primary transition-colors flex items-center gap-3">
                  <Avatar className="w-12 h-12 border border-border"><AvatarImage src={w.avatar_url} className="object-cover" /><AvatarFallback>{w.name?.[0]}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-bold truncate">{w.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{w.headline}</div>
                    {w.location && <div className="text-xs text-muted-foreground inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{w.location}</div>}
                  </div>
                  <ExternalLink className="w-4 h-4 text-primary shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Resources */}
        {data.resources?.length > 0 && (
          <section className="mt-8">
            <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4">Resources</h2>
            <div className="space-y-2">
              {data.resources.map((r) => (
                <a key={r.url} href={r.url} target="_blank" rel="noreferrer"
                   className="block rounded-lg border border-border bg-card p-4 hover:border-primary text-sm">
                  <div className="inline-flex items-center gap-2 font-semibold"><ExternalLink className="w-4 h-4 text-primary" /> {r.title}</div>
                </a>
              ))}
            </div>
          </section>
        )}
      </div>

      <JoinDialog
        open={joinOpen} onOpenChange={setJoinOpen} community={data}
        isEmployer={isEmployer} isWorker={isWorker}
        onDone={() => { setJoinOpen(false); load(); }}
      />
    </div>
  );
}

const JoinDialog = ({ open, onOpenChange, community, isEmployer, isWorker, onDone }) => {
  const [role, setRole] = useState(isEmployer ? "pledged_employer" : "member");
  const [pledge, setPledge] = useState(false);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setRole(isEmployer ? "pledged_employer" : "member"); setPledge(false); setVisible(false); } }, [open, isEmployer]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = isEmployer
        ? { role: "pledged_employer", pledge_accepted: pledge }
        : { role, show_on_resume: visible };
      const r = await joinCommunity(community.slug, payload);
      if (r.already_member) toast.info("You're already in this community");
      else toast.success(isEmployer ? "Pledge accepted — thank you." : "You've joined — welcome!");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not join");
    }
    setBusy(false);
  };

  const ac = community && accentClasses(community.accent);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="join-community-dialog">
        <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
          {community && <span className={`w-8 h-8 rounded-lg ${ac.bg} ${ac.text} flex items-center justify-center`}><CommunityIcon name={community.icon} className="w-4 h-4" /></span>}
          {isEmployer ? "Take the pledge" : `Join ${community?.name}`}
        </DialogTitle>
        <DialogDescription>
          {isEmployer ? "Publicly commit to inclusive hiring practices in this community." : "You control what's visible. Nothing is shared until you choose to."}
        </DialogDescription>

        {isEmployer ? (
          <div className="mt-2 space-y-4">
            <div className="rounded-lg border border-border bg-accent p-4 text-sm space-y-2" data-testid="pledge-terms">
              <p className="font-bold text-foreground">The {community?.name} pledge:</p>
              <ul className="list-disc pl-5 space-y-1 text-foreground/85">
                <li>We commit to inclusive, skills-first hiring across this community.</li>
                <li>We will consider reasonable accommodations and flexible arrangements.</li>
                <li>We will not tolerate discrimination or harassment in our workplace.</li>
                <li>We understand a pledge is a public commitment and workers may share their experiences with us.</li>
              </ul>
            </div>
            <label className="flex items-start gap-3 text-sm cursor-pointer">
              <input data-testid="pledge-checkbox" type="checkbox" checked={pledge} onChange={(e) => setPledge(e.target.checked)}
                     className="mt-1 w-4 h-4 accent-primary" />
              <span>I accept this pledge on behalf of my organization and understand it will be shown publicly on my employer profile.</span>
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button data-testid="pledge-confirm-btn" disabled={!pledge || busy} onClick={submit}>
                <Check className="w-4 h-4 mr-1.5" /> Take the pledge
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-2 space-y-4">
            {isWorker && (
              <div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">Join as</p>
                <div className="grid grid-cols-2 gap-2">
                  <RoleBtn testid="role-member" active={role === "member"} onClick={() => setRole("member")}
                           title="Member" desc="I identify with this community." />
                  <RoleBtn testid="role-ally" active={role === "ally"} onClick={() => setRole("ally")}
                           title="Ally" desc="I support this community." />
                </div>
              </div>
            )}
            <div className="rounded-lg border border-border bg-card p-4 flex items-start gap-3">
              <div className="flex-1">
                <Label className="text-sm font-semibold flex items-center gap-1.5">
                  {visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />} Show on my public resume
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Off by default. When off, membership is private — only you can see it. You can change this anytime.
                </p>
              </div>
              <Switch data-testid="visibility-switch" checked={visible} onCheckedChange={setVisible} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button data-testid="join-confirm-btn" disabled={busy} onClick={submit}>Join</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const RoleBtn = ({ testid, active, onClick, title, desc }) => (
  <button data-testid={testid} type="button" onClick={onClick}
          className={`rounded-xl border-2 text-left p-3 transition-colors ${active ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>
    <div className="font-semibold">{title}</div>
    <div className="text-xs text-muted-foreground">{desc}</div>
  </button>
);
